// ============================================================================
// PARTNER ECOSYSTEM JOIN UTILITY
// ============================================================================
//
// PURPOSE: Shared function to join the partner ecosystem
// USAGE: Can be imported and used in any app
//
// ============================================================================

export interface JoinPartnerEcosystemOptions {
  email: string;
  name: string;
  apiUrl?: string; // Optional custom API URL, defaults to current origin
}

export interface JoinPartnerEcosystemResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Join the WTC Partner Ecosystem
 * 
 * Sends user email and name to the backend API which:
 * 1. Sends a welcome email with Slack invite link
 * 2. Posts user data to Slack webhook for list management
 * 
 * @param options - { email, name, apiUrl? }
 * @returns Promise with success status and message/error
 * 
 * @example
 * import { joinPartnerEcosystem } from '@wtc/ui'
 * 
 * const result = await joinPartnerEcosystem({
 *   email: 'user@example.com',
 *   name: 'John Doe'
 * })
 * 
 * if (result.success) {
 *   console.log(result.message)
 * } else {
 *   console.error(result.error)
 * }
 */
export async function joinPartnerEcosystem(
  options: JoinPartnerEcosystemOptions
): Promise<JoinPartnerEcosystemResponse> {
  try {
    const { email, name, apiUrl } = options;

    // Validate input
    if (!email || !name) {
      return {
        success: false,
        error: 'Email and name are required',
      };
    }

    // Determine API URL
    let endpoint = '/api/partner-ecosystem/join';
    if (apiUrl) {
      endpoint = `${apiUrl}/api/partner-ecosystem/join`;
    } else if (typeof window !== 'undefined') {
      // Client-side: use current origin
      endpoint = `${window.location.origin}/api/partner-ecosystem/join`;
    }

    // Call the API
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, name }),
    });

    const data = (await response.json()) as JoinPartnerEcosystemResponse;

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to join partner ecosystem',
      };
    }

    return data;
  } catch (error) {
    console.error('Error joining partner ecosystem:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An error occurred',
    };
  }
}
